import { useApplicantAPI } from "@certn/api";
import { GhostButton, Stack } from "@certn/cascadia";
import { Container } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useState } from "react";
import { Trans } from "react-i18next";

import { Address } from "@certn/cascadia/patterns/address-form/types";

import { PaymentCard } from "./PaymentCard";
import { AddPaymentProfileModal } from "./AddPaymentProfileModal";

interface Props {
  onPaymentProfileSelected?: () => void;
  defaultAddress?: Address;
}

export function PaymentProfileList(props: Props) {
  const { onPaymentProfileSelected, defaultAddress } = props;
  // Filter to ACTIVE profiles on the backend for efficiency
  // Note: There can only be ONE ACTIVE profile at a time
  const { data: paymentProfilesData, mutate } = useApplicantAPI(
    "/api/internal/applicant/me/payment-profiles/",
    { status: "ACTIVE" }
  );
  // Unfiltered total — ACTIVE-filtered `count` can stay flat when replacing
  // the single active card (DAT-1731 polling baseline).
  const { data: allPaymentProfilesData, mutate: mutateAllPaymentProfiles } =
    useApplicantAPI("/api/internal/applicant/me/payment-profiles/");
  const [showNewPaymentModal, setShowNewPaymentModal] = useState(false);

  // Backend now filters to ACTIVE profiles, so no frontend filtering needed
  const paymentProfiles = paymentProfilesData?.results || [];
  // Since there can only be one ACTIVE profile, get the first (and only) one
  const activeProfile = paymentProfiles[0];

  const handleAddNewPayment = () => {
    // Wait until the unfiltered total is known so polling baseline isn't 0
    // while the request is still in flight (DAT-1731 Bugbot).
    if (typeof allPaymentProfilesData?.count !== "number") return;
    setShowNewPaymentModal(true);
  };

  const handleCloseModal = async () => {
    setShowNewPaymentModal(false);
    // Refresh both ACTIVE and unfiltered caches so the next add uses a
    // correct polling baseline (DAT-1731 Bugbot).
    await Promise.all([mutate(), mutateAllPaymentProfiles()]);
    onPaymentProfileSelected?.();
  };

  return (
    <Container className={styles.container}>
      {activeProfile && <PaymentCard profile={activeProfile} />}
      <Stack orientation="horizontal" justify="space-between" gap="space-4">
        <GhostButton
          size="medium"
          onPress={handleAddNewPayment}
          isDisabled={typeof allPaymentProfilesData?.count !== "number"}
        >
          <Trans>Add new</Trans>
        </GhostButton>
      </Stack>
      {showNewPaymentModal &&
        typeof allPaymentProfilesData?.count === "number" && (
          <AddPaymentProfileModal
            handleClose={handleCloseModal}
            defaultAddress={defaultAddress}
            initialPaymentProfileCount={allPaymentProfilesData.count}
          />
        )}
    </Container>
  );
}

const styles = {
  container: css({
    maxWidth: "100% !important",
    gap: "var(--space-4)",
    width: "unset",
    paddingLeft: "var(--space-4)",
    paddingRight: "var(--space-4)",
    paddingBottom: "var(--space-0)",
  }),
};
